import React from 'react';
import { FileText, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useDocumentTemplates } from './documentTemplates/useDocumentTemplates';
import { TemplateCard } from './documentTemplates/TemplateCard';
import { CreateTemplateDialog } from './documentTemplates/CreateTemplateDialog';
import { EditTemplateDialog } from './documentTemplates/EditTemplateDialog';
import { PreviewTemplateDialog } from './documentTemplates/PreviewTemplateDialog';
import { PublishTemplateDialog } from './documentTemplates/PublishTemplateDialog';
import { DeleteTemplateDialog } from './documentTemplates/DeleteTemplateDialog';

export default function DocumentTemplates() {
  const d = useDocumentTemplates();

  if (d.loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="document-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Templates</h1>
          <p className="text-gray-500 mt-1">Manage client agreements, terms, and policies</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={d.openCreate} data-testid="create-template-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Document
        </Button>
      </div>

      {d.templates.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium text-gray-500">No document templates yet</p>
          <p className="text-sm mt-1">Click "Add Document" to create your first template</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {d.templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onPreview={d.openPreview}
              onEdit={d.openEdit}
              onPublish={d.openPublish}
              onDelete={d.openDelete}
            />
          ))}
        </div>
      )}

      <CreateTemplateDialog
        open={d.createDialog}
        onOpenChange={d.setCreateDialog}
        createTitle={d.createTitle}
        setCreateTitle={d.setCreateTitle}
        createDocType={d.createDocType}
        setCreateDocType={d.setCreateDocType}
        createContent={d.createContent}
        setCreateContent={d.setCreateContent}
        createExpiresInDays={d.createExpiresInDays}
        setCreateExpiresInDays={d.setCreateExpiresInDays}
        creating={d.creating}
        onCreate={d.handleCreate}
      />

      <EditTemplateDialog
        open={d.editDialog}
        onOpenChange={d.setEditDialog}
        editTitle={d.editTitle}
        setEditTitle={d.setEditTitle}
        editContent={d.editContent}
        setEditContent={d.setEditContent}
        editExpiresInDays={d.editExpiresInDays}
        setEditExpiresInDays={d.setEditExpiresInDays}
        saving={d.saving}
        onSave={d.handleSave}
      />

      <PreviewTemplateDialog
        open={d.previewDialog}
        onOpenChange={d.setPreviewDialog}
        template={d.activeTemplate}
      />

      <PublishTemplateDialog
        open={d.publishDialog}
        onOpenChange={d.setPublishDialog}
        template={d.activeTemplate}
        clients={d.clients}
        publishMode={d.publishMode}
        setPublishMode={d.setPublishMode}
        selectedClients={d.selectedClients}
        toggleClientSelect={d.toggleClientSelect}
        addendumSummary={d.addendumSummary}
        setAddendumSummary={d.setAddendumSummary}
        publishing={d.publishing}
        onPublish={d.handlePublish}
      />

      <DeleteTemplateDialog
        open={d.deleteDialog}
        onOpenChange={d.setDeleteDialog}
        template={d.activeTemplate}
        deleting={d.deleting}
        onDelete={d.handleDelete}
      />
    </div>
  );
}
